import { useState } from "react";
import api from "@/lib/api";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { toast } from "sonner";
import { Gate, ready, Panel, useLoad, cad, stamp, Chip, Table, Filters, Modal, fieldStyle, Label } from "@/components/console/ui";
import { openFile } from "@/components/undertake/ReviewTab";

export function Payments() {
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("paid");
  const [rows] = useLoad(`/console/payments?kind=${kind}&status=${status}`, 15000);
  const total = (ready(rows) ? rows : []).reduce((a, r) => a + r.amount, 0);
  const commission = (ready(rows) ? rows : []).reduce((a, r) => a + r.commission, 0);
  return (
    <Panel title="payments ledger" testId="console-payments" right={<span className="text-xs" data-testid="ledger-total">{ready(rows) ? rows.length : 0} rows · {cad(total)} cad · fee {cad(commission)}</span>}>
      <div className="flex flex-wrap gap-6 mb-4">
        <Filters testId="ledger-kind" value={kind} onChange={setKind} options={[["", "all"], ["provisions", "provisions"], ["sentence", "reduce/extend"], ["sentence_item", "help/punish"]]} />
        <Filters testId="ledger-status" value={status} onChange={setStatus} options={[["paid", "paid"], ["pending", "pending"], ["", "any status"]]} />
      </div>
      <Table testId="ledger-table" rowKey="session_id" rows={rows} empty="no payments match"
        cols={[
          { k: "created_at", label: "when", render: (r) => stamp(r.created_at), cls: "whitespace-nowrap" },
          { k: "user", label: "who" },
          { k: "label", label: "what" },
          { k: "amount", label: "cad", render: (r) => <span style={{ color: "var(--accent)" }}>{cad(r.amount)}</span> },
          { k: "commission", label: "fee", render: (r) => (r.commission ? cad(r.commission) : "—") },
          { k: "status", label: "status", render: (r) => <Chip on={r.status === "paid"}>{r.status}</Chip> },
          { k: "session_id", label: "stripe ref", render: (r) => <span className="opacity-50">{String(r.session_id || "—").slice(0, 18)}…</span> },
        ]} />
    </Panel>
  );
}

function FailModal({ u, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/undertakings/${u.id}/fail`, { reason }); toast.success("failed · back to the pile"); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal title="fail undertaking" onClose={onClose} testId="verify-fail-modal">
      <Label>reason (shown to the undertaker) · {countWords(reason)}/33</Label>
      <textarea data-testid="verify-fail-reason" value={reason} onChange={(e) => setReason(limitWords(e.target.value, reason, 33))} rows={3} className="w-full p-3 text-sm resize-none" style={fieldStyle} />
      <button onClick={submit} disabled={busy} data-testid="verify-fail-confirm" className="btn w-full py-3 mt-4 text-xs tracking-widest">{busy ? "…" : "confirm fail"}</button>
    </Modal>
  );
}

export function Verify() {
  const [rows, reload] = useLoad("/undertakings/review", 8000);
  const [failing, setFailing] = useState(null);
  const [busy, setBusy] = useState(null);
  const approve = async (u) => {
    if (busy) return;
    setBusy(u.id);
    try { await api.post(`/undertakings/${u.id}/complete`); toast.success(`approved · ${u.provisions} provisions released`); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <Panel title="verification queue · oldest first" testId="console-verify" right={<span className="text-xs">{ready(rows) ? rows.length : "…"} waiting</span>}>
      {!ready(rows) ? <Gate data={rows} testId="verify" /> : !rows.length ? <p className="text-xs opacity-60 py-6" data-testid="verify-empty">queue empty · no proof waiting</p> : (
        <ul className="flex flex-col gap-4">
          {rows.map((u) => (
            <li key={u.id} className="p-4" style={{ border: "1px solid var(--border)", background: "var(--bg-primary)" }} data-testid={`verify-item-${u.id}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-xs">{u.provisions} provisions held in escrow</span>
                <span className="text-xs opacity-60 ml-auto">sent up {stamp(u.sent_up_at)}</span>
              </div>
              <p className="long-text-sm text-sm leading-relaxed mb-3">{u.text}</p>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3 opacity-80">
                <span>delegator · <span style={{ color: "var(--accent)" }}>{u.author?.name || "—"}</span></span>
                <span>undertaker · <span style={{ color: "var(--accent)" }}>{u.undertaker?.name || "—"}</span></span>
              </div>
              <Label>proof files</Label>
              <div className="flex flex-wrap gap-2 mb-4">
                {(u.files || []).length ? u.files.map((f) => <button key={f.id} onClick={() => openFile(u.id, f)} data-testid={`verify-file-${f.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">↓ {f.name || "file"}</button>) : <span className="text-xs opacity-60">no files</span>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setFailing(u)} data-testid={`verify-fail-${u.id}`} className="btn py-3 text-xs tracking-widest">fail</button>
                <button onClick={() => approve(u)} disabled={busy === u.id} data-testid={`verify-approve-${u.id}`} className="btn py-3 text-xs tracking-widest">{busy === u.id ? "…" : "approve & release"}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {failing && <FailModal u={failing} onClose={() => setFailing(null)} onDone={() => { setFailing(null); reload(); }} />}
    </Panel>
  );
}

export function Cashouts() {
  const [rows, reload] = useLoad("/cashouts", 15000);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);
  const act = async (c, kind) => {
    if (busy) return;
    if (kind === "reject" && !window.confirm("reject this cash out? provisions return to the user.")) return;
    setBusy(c.id);
    try { await api.post(`/cashouts/${c.id}/${kind}`); toast.success(kind === "paid" ? "marked paid" : "rejected · provisions returned"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const shown = !ready(rows) ? rows : rows.filter((c) => !filter || c.status === filter);
  return (
    <Panel title="cash out requests" testId="console-cashouts" right={<Filters testId="cashout-filter" value={filter} onChange={setFilter} options={[["pending", "pending"], ["paid", "paid"], ["rejected", "rejected"], ["", "all"]]} />}>
      <Table testId="cashout-table" rows={shown} empty="no cash outs here"
        cols={[
          { k: "created_at", label: "requested", render: (c) => stamp(c.created_at), cls: "whitespace-nowrap" },
          { k: "user_name", label: "user" },
          { k: "amount", label: "provisions" },
          { k: "cad", label: "send", render: (c) => <span style={{ color: "var(--accent)" }}>{cad(c.amount)} cad</span> },
          { k: "payout_email", label: "e-transfer / paypal", render: (c) => <span className="break-all">{c.payout_email}</span> },
          { k: "status", label: "status", render: (c) => <Chip on={c.status === "paid"}>{c.status}</Chip> },
          { k: "act", label: "", render: (c) => c.status === "pending" && (
            <div className="flex gap-2 justify-end">
              <button onClick={() => act(c, "reject")} disabled={busy === c.id} data-testid={`cashout-reject-${c.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">reject</button>
              <button onClick={() => act(c, "paid")} disabled={busy === c.id} data-testid={`cashout-paid-${c.id}`} className="btn px-3 py-1 text-[10px] tracking-widest">mark paid</button>
            </div>
          ) },
        ]} />
    </Panel>
  );
}
